import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '@mantine/core';
import { useFieldArray, useForm, FormProvider, useWatch } from 'react-hook-form';
import { SourcesUsesSecuritiesTable } from '@dealstack/frontend/app/pages/Valuations/views/SourcesAndUses/components/SourcesUsesSecuritiesTable';
import {
	PersonSummary,
	ValuationPersonInputRes,
	ValuationSummaryGroupByPersonRes,
	CurrencyType,
	OverdistributionErrorSourceEnum,
} from '@dealstack/shared/lib/generated';
import { TextV2 } from '@dealstack/frontend/shared/components/TextV2/TextV2';
import {
	SourcesUsesTableHeader,
	SourcesUsesTableWrapper,
} from '@dealstack/frontend/app/pages/Valuations/views/SourcesAndUses/styled';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import {
	CustomRow,
	EnhancedFormValue,
	PersonInputForm,
	PersonInputFormValue,
} from '@dealstack/frontend/app/pages/Valuations/views/SourcesAndUses/types';
import * as uuid from 'uuid';
import {
	SourcesUsesCategories,
	SourcesUsesUndistributedRowId,
} from '@dealstack/frontend/app/pages/Valuations/views/SourcesAndUses/shared/SourcesUsesTable';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslationValuations } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { ValuationsValidationError } from '@dealstack/frontend/app/pages/Valuations/components/ValuationsWarnings/ValuationsWarnings.utils';
import { HighPrecisionDecimal } from '@dealstack/shared/lib/format/currency/highPrecisionDecimal';
import {
	makeComplexDataTestId,
	VALUATION_ELEMENT_ID,
} from '@dealstack/shared/tests/testElementIds';
import { PersonInputSavePayload } from '@dealstack/frontend/app/pages/Valuations/queries/usePersonInputsQuery';

interface Props {
	category: string;
	categoryId: SourcesUsesCategories;
	selectedEntity: PersonSummary;
	summaryData: ValuationSummaryGroupByPersonRes;
	formId: string;
	nameColumnTitle?: string;
	amountColumnTitle?: string;
	entityId?: string;
	mipId?: string;
	baseCurrency: CurrencyType;
	fxRates: Array<{ currency: CurrencyType; rate: number }>;
	validationErrors: ValuationsValidationError[];
	personInputs: ValuationPersonInputRes[] | undefined;
	isLoadingPerson: boolean;
	saveFields: (snapshot: PersonInputSavePayload[]) => void;
	deleteField: (personId: string, inputId: string) => Promise<void>;
	flushPersonInput: () => void;
	getPersonInputsByCategory: (categoryId: string) => PersonInputFormValue[];
}

const SOURCES_CATEGORIES = new Set<SourcesUsesCategories>([
	SourcesUsesCategories.Receivables,
	SourcesUsesCategories.CashOnBalanceSheet,
]);

const USES_CATEGORIES = new Set<SourcesUsesCategories>([
	SourcesUsesCategories.Payables,
	SourcesUsesCategories.RetainedCashAndHoldbacks,
]);

function buildSnapshot(
	rows: EnhancedFormValue[],
	categoryId: SourcesUsesCategories
): PersonInputSavePayload[] {
	return rows
		.filter((row) => !!row.name?.trim() && !!row.value?.currencyAmount)
		.map((row) => ({
			id: row.personInputId,
			categoryId,
			value: {
				name: row.name,
				value: {
					currencyAmount: row.value.currencyAmount,
					currency: row.value.currency,
				},
				personInputId: row.personInputId,
				localId: row.localId,
			},
		}));
}

export const PersonInputFormContainer: React.FC<Props> = ({
	category,
	categoryId,
	selectedEntity,
	summaryData,
	formId,
	mipId,
	baseCurrency,
	fxRates,
	validationErrors,
	personInputs,
	isLoadingPerson,
	saveFields,
	deleteField,
	flushPersonInput,
	getPersonInputsByCategory,
}) => {
	const [isDeleting, setIsDeleting] = useState(false);
	const tValuations = useTranslationValuations();

	const undistributedCash = useMemo(() => {
		const selectedEntityData = selectedEntity.groups.find((group) => group.id === mipId);
		return new HighPrecisionDecimal(selectedEntityData?.retained.total ?? 0).sub(
			selectedEntityData?.retained.cash ?? 0
		);
	}, [selectedEntity, mipId]);

	const warrantReceivablesBreakdown = useMemo(() => {
		return selectedEntity.groups
			.filter((g) => !mipId || g.id === mipId)
			.flatMap((g) => g.proceeds?.receivablesBreakdown ?? []);
	}, [selectedEntity, mipId]);

	const warrantPayablesBreakdown = useMemo(() => {
		return selectedEntity.groups
			.filter((g) => !mipId || g.id === mipId)
			.flatMap((g) => g.outflows?.payablesBreakdown ?? []);
	}, [selectedEntity, mipId]);

	const methods = useForm<PersonInputForm>({
		defaultValues: {
			inputs: [],
		},
		mode: 'onChange',
		resolver: zodResolver(
			z.object({
				inputs: z.array(
					z.object({
						name: z.string().min(1),
						value: z.object({
							currencyAmount: z
								.string()
								.regex(/^\+?((\d{1,10}(\.(?!\.))?|(?=\.))(\.\d{1,5})?)$/),
							currency: z.nativeEnum(CurrencyType),
						}),
					})
				),
			})
		),
	});

	const { fields, append, remove } = useFieldArray({
		control: methods.control,
		name: 'inputs',
	});

	const formValues = useWatch({
		control: methods.control,
		name: 'inputs',
	}) as EnhancedFormValue[];

	useEffect(
		function updateFormOnCategoryDataChange() {
			if (isDeleting) {
				return;
			}

			const categoryInputs = getPersonInputsByCategory(categoryId);
			const currentRows = (methods.getValues().inputs ?? []) as EnhancedFormValue[];

			// Carry any local-only rows the server doesn't yet know about (user
			// added but the save hasn't promoted them to server-id rows).
			const localOnly = currentRows.filter(
				(row) =>
					row.localId &&
					!row.personInputId &&
					!categoryInputs.some(
						(server) =>
							server.name === row.name &&
							server.value.currency === row.value.currency &&
							Number(server.value.currencyAmount) === Number(row.value.currencyAmount)
					)
			);

			methods.reset({ inputs: [...categoryInputs, ...localOnly] });
		},
		[
			selectedEntity.id,
			categoryId,
			getPersonInputsByCategory,
			methods,
			personInputs,
			isDeleting,
		]
	);

	const triggerSave = useCallback(() => {
		const snapshot = buildSnapshot(
			methods.getValues().inputs as EnhancedFormValue[],
			categoryId
		);
		if (snapshot.length === 0) {
			return;
		}
		saveFields(snapshot);
	}, [methods, categoryId, saveFields]);

	const handleFieldChange = useCallback(
		(index: number, fieldPath: string, newValue: unknown) => {
			const currentField = methods.getValues(`inputs.${index}`);
			if (!currentField) {
				return;
			}

			const updatedField = { ...currentField };

			if (fieldPath === 'name') {
				updatedField.name = newValue as string;
			} else if (fieldPath === 'value') {
				updatedField.value = newValue as {
					currencyAmount: string;
					currency: CurrencyType;
				};
			}

			methods.setValue(`inputs.${index}`, updatedField);
			triggerSave();
		},
		[methods, triggerSave]
	);

	const handleFieldBlur = useCallback(() => {
		flushPersonInput();
	}, [flushPersonInput]);

	const handleAddNewRow = useCallback(() => {
		append(
			{
				name: '',
				value: {
					currencyAmount: '0.00',
					currency: baseCurrency,
				},
				personInputId: undefined,
				localId: uuid.v4(),
			},
			{ shouldFocus: true }
		);
	}, [append, baseCurrency]);

	const handleDeleteRow = useCallback(
		async (rowId: string) => {
			const index = Number.parseInt(rowId.split('custom')[1]);
			if (Number.isNaN(index) || index < 0 || index > fields.length - 1) {
				return;
			}

			setIsDeleting(true);
			const field = fields[index] as EnhancedFormValue;
			remove(index);

			if (field.personInputId) {
				await deleteField(selectedEntity.id, field.personInputId);
			}

			setIsDeleting(false);
		},
		[fields, remove, selectedEntity.id, deleteField]
	);

	const customRows: CustomRow[] = useMemo(
		() => [
			...fields.map((_, index) => {
				const fieldValue = formValues?.[index];

				if (!fieldValue) {
					return {
						id: `custom${index}`,
						name: '',
						amount: new HighPrecisionDecimal(0),
						currency: baseCurrency,
						pending: false,
						disabled: false,
						index,
					};
				}

				return {
					id: `custom${index}`,
					name: fieldValue.name ?? '',
					amount: new HighPrecisionDecimal(fieldValue.value?.currencyAmount ?? 0),
					personInputId: fieldValue.personInputId,
					localId: fieldValue.localId,
					currency: fieldValue.value?.currency ?? baseCurrency,
					pending: false,
					disabled: false,
					index,
				};
			}),
			...(categoryId === SourcesUsesCategories.RetainedCashAndHoldbacks &&
			undistributedCash.greaterThan(0)
				? [
						{
							id: SourcesUsesUndistributedRowId,
							name: tValuations('sources_and_uses.undistributed_cash'),
							amount: undistributedCash,
							currency: baseCurrency,
						},
					]
				: []),
			...(categoryId === SourcesUsesCategories.Receivables
				? (() => {
						const grouped = new Map<
							string,
							{
								exchangeSecurityId: string;
								entries: typeof warrantReceivablesBreakdown;
							}
						>();
						for (const entry of warrantReceivablesBreakdown) {
							const key = entry.exchangeSecurityName;
							if (!grouped.has(key)) {
								grouped.set(key, {
									exchangeSecurityId: entry.exchangeSecurityId,
									entries: [],
								});
							}
							grouped.get(key)?.entries.push(entry);
						}
						return Array.from(grouped.entries()).map(
							([warrantName, { exchangeSecurityId, entries }]) => {
								const totalAmount = entries.reduce(
									(sum, e) => sum.add(e.amount),
									new HighPrecisionDecimal(0)
								);
								const subRows = entries.map((entry, idx) => {
									const holderName =
										summaryData?.persons.find((p) => p.id === entry.holderId)
											?.displayName ?? 'Unknown';
									return {
										id: `warrant-receivables-${exchangeSecurityId}-${idx}`,
										name: holderName,
										amount: new HighPrecisionDecimal(entry.amount),
										currency: baseCurrency,
										disabled: true,
									};
								});
								return {
									id: `warrant-receivables-${exchangeSecurityId}`,
									name: `${warrantName} (exercise price)`,
									amount: totalAmount,
									currency: baseCurrency,
									disabled: true,
									excludedFromTotal: entries.every((e) => e.excludedFromTotal),
									expandableCount: entries.length,
									subRows,
								};
							}
						);
					})()
				: []),
			...(categoryId === SourcesUsesCategories.Payables
				? (() => {
						const grouped = new Map<
							string,
							{ exchangeSecurityId: string; entries: typeof warrantPayablesBreakdown }
						>();
						for (const entry of warrantPayablesBreakdown) {
							const key = entry.exchangeSecurityName;
							if (!grouped.has(key)) {
								grouped.set(key, {
									exchangeSecurityId: entry.exchangeSecurityId,
									entries: [],
								});
							}
							grouped.get(key)?.entries.push(entry);
						}
						return Array.from(grouped.entries()).map(
							([warrantName, { exchangeSecurityId, entries }]) => {
								const totalAmount = entries.reduce(
									(sum, e) => sum.add(e.amount),
									new HighPrecisionDecimal(0)
								);
								const subRows = entries.map((entry, idx) => {
									const issuerName =
										summaryData?.persons.find((p) => p.id === entry.issuerId)
											?.displayName ?? 'Unknown';
									return {
										id: `warrant-payables-${exchangeSecurityId}-${idx}`,
										name: issuerName,
										amount: new HighPrecisionDecimal(entry.amount),
										currency: baseCurrency,
										disabled: true,
									};
								});
								return {
									id: `warrant-payables-${exchangeSecurityId}`,
									name: `${warrantName} (exercise price)`,
									amount: new HighPrecisionDecimal(totalAmount),
									currency: baseCurrency,
									disabled: true,
									excludedFromTotal: entries.every((e) => e.excludedFromTotal),
									expandableCount: entries.length,
									subRows,
								};
							}
						);
					})()
				: []),
		],
		[
			fields,
			formValues,
			baseCurrency,
			undistributedCash,
			warrantReceivablesBreakdown,
			warrantPayablesBreakdown,
			categoryId,
			tValuations,
			summaryData,
		]
	);

	const isErrorRelevantToCurrentCategory = (error: ValuationsValidationError) => {
		const isCorrectPerson = selectedEntity.id === error.personId && mipId === error.mipId;
		if (!isCorrectPerson) {
			return false;
		}

		const isSourcesCategory = SOURCES_CATEGORIES.has(categoryId);
		const isUsesCategory = USES_CATEGORIES.has(categoryId);

		return (
			(isSourcesCategory && error.sourceType === OverdistributionErrorSourceEnum.SOURCES) ||
			(isUsesCategory && error.sourceType === OverdistributionErrorSourceEnum.USES)
		);
	};

	const hasError =
		validationErrors.some(isErrorRelevantToCurrentCategory) && formValues.length > 0;

	return (
		<SourcesUsesTableWrapper
			data-testid={makeComplexDataTestId(
				VALUATION_ELEMENT_ID.SOURCES_AND_USES_TABLE_CATEGORY,
				category
			)}
			hasError={hasError}
			style={{ overflow: 'visible' }}
		>
			<SourcesUsesTableHeader>
				<TextV2 variant="sans/14/600">{category}</TextV2>
				<Button
					data-testid={VALUATION_ELEMENT_ID.SOURCES_AND_USES_TABLE_CATEGORY_BTN}
					sx={{ height: '2rem' }}
					variant="outline"
					onClick={handleAddNewRow}
					type="button"
				>
					<Icon variant="plus" size="md" style={{ marginRight: '0.4rem' }} />
					<TextV2 variant="sans/11/600">
						{tValuations('sources_and_uses.add_new_button')}
					</TextV2>
				</Button>
			</SourcesUsesTableHeader>
			<FormProvider {...methods}>
				<form
					id={formId}
					style={{ flexGrow: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
				>
					<SourcesUsesSecuritiesTable
						person={selectedEntity}
						summary={summaryData}
						customRows={customRows}
						baseCurrency={baseCurrency}
						fxRates={fxRates}
						isLoading={isLoadingPerson}
						onDeleteRow={handleDeleteRow}
						onFieldChange={handleFieldChange}
						onFieldBlur={handleFieldBlur}
						variant="inputs"
					/>
				</form>
			</FormProvider>
		</SourcesUsesTableWrapper>
	);
};
